import { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';

import { useTranslation } from '../common/components/LocalizationProvider';
import { poisActions } from '../store';
import fetchOrThrow from '../common/util/fetchOrThrow';

// Campos do POI que podem ser mapeados
const POI_FIELDS = [
  { id: 'name', label: 'Nome', required: true },
  { id: 'latitude', label: 'Latitude', required: true },
  { id: 'longitude', label: 'Longitude', required: true },
  { id: 'description', label: 'Descricao', required: false },
  { id: 'address', label: 'Endereco', required: false },
  { id: 'city', label: 'Cidade', required: false },
  { id: 'state', label: 'Estado', required: false },
  { id: 'postalCode', label: 'CEP', required: false },
  { id: 'contactName', label: 'Nome Contato', required: false },
  { id: 'contactPhone', label: 'Telefone', required: false },
  { id: 'contactEmail', label: 'Email', required: false },
  { id: 'color', label: 'Cor (#RRGGBB)', required: false },
  { id: 'radius', label: 'Raio (metros)', required: false },
];

const MAX_POIS = 1000;
const PREVIEW_ROWS = 5;

const PoiImportDialog = ({ open, onClose, onSuccess }) => {
  const t = useTranslation();
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [rawData, setRawData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [validationResults, setValidationResults] = useState({ valid: [], errors: [] });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const steps = [
    t('poiImportStepFile') || 'Selecionar Arquivo',
    t('poiImportStepMapping') || 'Mapear Colunas',
    t('poiImportStepPreview') || 'Preview e Importar',
  ];

  // Reset state when dialog opens/closes
  const handleClose = useCallback(() => {
    setActiveStep(0);
    setFile(null);
    setRawData([]);
    setHeaders([]);
    setMapping({});
    setValidationResults({ valid: [], errors: [] });
    setImporting(false);
    setImportResult(null);
    onClose();
  }, [onClose]);

  // Parse uploaded file
  const parseFile = useCallback((uploadedFile) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        if (jsonData.length < 2) {
          throw new Error('Arquivo deve ter pelo menos 2 linhas (cabecalho + dados)');
        }

        // First row is headers
        const fileHeaders = jsonData[0].map((h, i) => ({
          index: i,
          name: String(h || `Coluna ${i + 1}`),
        }));

        // Rest is data (limit to MAX_POIS)
        const fileData = jsonData.slice(1, MAX_POIS + 1);

        setHeaders(fileHeaders);
        setRawData(fileData);
        setFile(uploadedFile);

        // Auto-map columns based on header names
        const autoMapping = {};
        POI_FIELDS.forEach((field) => {
          const match = fileHeaders.find((h) =>
            h.name.toLowerCase().includes(field.id.toLowerCase())
            || h.name.toLowerCase().includes(field.label.toLowerCase()));
          if (match) {
            autoMapping[field.id] = match.index;
          }
        });
        setMapping(autoMapping);

        setActiveStep(1);
      } catch (error) {
        console.error('Error parsing file:', error);
        alert(`Erro ao ler arquivo: ${error.message}`);
      }
    };

    reader.readAsArrayBuffer(uploadedFile);
  }, []);

  // Handle file drop
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const ext = droppedFile.name.split('.').pop().toLowerCase();
      if (['csv', 'xlsx', 'xls'].includes(ext)) {
        parseFile(droppedFile);
      } else {
        alert('Formato nao suportado. Use CSV, XLSX ou XLS.');
      }
    }
  }, [parseFile]);

  // Handle file select
  const handleFileSelect = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      parseFile(selectedFile);
    }
  }, [parseFile]);

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  // Update column mapping
  const handleMappingChange = useCallback((fieldId, columnIndex) => {
    setMapping((prev) => ({
      ...prev,
      [fieldId]: columnIndex === '' ? undefined : columnIndex,
    }));
  }, []);

  // Validate data based on mapping
  const validateData = useCallback(() => {
    const valid = [];
    const errors = [];

    rawData.forEach((row, rowIndex) => {
      const poi = {};
      const rowErrors = [];

      // Map columns to POI fields
      POI_FIELDS.forEach((field) => {
        const colIndex = mapping[field.id];
        if (colIndex !== undefined && row[colIndex] !== undefined) {
          poi[field.id] = row[colIndex];
        }
      });

      // Validate required fields
      if (!poi.name || String(poi.name).trim() === '') {
        rowErrors.push('Nome obrigatorio');
      }

      // Validate latitude
      const lat = parseFloat(poi.latitude);
      if (Number.isNaN(lat) || lat < -90 || lat > 90) {
        rowErrors.push(`Latitude invalida: ${poi.latitude}`);
      } else {
        poi.latitude = lat;
      }

      // Validate longitude
      const lng = parseFloat(poi.longitude);
      if (Number.isNaN(lng) || lng < -180 || lng > 180) {
        rowErrors.push(`Longitude invalida: ${poi.longitude}`);
      } else {
        poi.longitude = lng;
      }

      // Validate optional numeric fields
      if (poi.radius) {
        const radius = parseInt(poi.radius, 10);
        if (!Number.isNaN(radius) && radius > 0) {
          poi.radius = radius;
        } else {
          poi.radius = 100; // default
        }
      }

      // Validate color format
      if (poi.color && !/^#[0-9A-Fa-f]{6}$/.test(poi.color)) {
        poi.color = '#3B82F6'; // default blue
      }

      if (rowErrors.length > 0) {
        errors.push({ line: rowIndex + 2, errors: rowErrors, data: poi });
      } else {
        valid.push(poi);
      }
    });

    setValidationResults({ valid, errors });
    setActiveStep(2);
  }, [rawData, mapping]);

  // Check if required fields are mapped
  const requiredFieldsMapped = useMemo(() => {
    const required = POI_FIELDS.filter((f) => f.required);
    return required.every((f) => mapping[f.id] !== undefined);
  }, [mapping]);

  // Import POIs
  const handleImport = useCallback(async () => {
    if (validationResults.valid.length === 0) return;

    setImporting(true);
    setImportResult(null);

    try {
      // Create POIs in batches of 50
      const batchSize = 50;
      const batches = [];
      for (let i = 0; i < validationResults.valid.length; i += batchSize) {
        batches.push(validationResults.valid.slice(i, i + batchSize));
      }

      let imported = 0;
      const importErrors = [];

      for (const batch of batches) {
        const promises = batch.map(async (poi) => {
          try {
            const response = await fetchOrThrow('/api/pois', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ...poi,
                visible: true,
              }),
            });
            return { success: true, data: await response.json() };
          } catch (error) {
            return { success: false, error: error.message, poi };
          }
        });

        const results = await Promise.all(promises);
        results.forEach((result) => {
          if (result.success) {
            imported += 1;
          } else {
            importErrors.push(result);
          }
        });
      }

      setImportResult({
        total: validationResults.valid.length,
        imported,
        errors: importErrors,
      });

      // Refresh POIs in store
      if (imported > 0) {
        const response = await fetchOrThrow('/api/pois');
        const pois = await response.json();
        dispatch(poisActions.refresh(pois));
        if (onSuccess) onSuccess(imported);
      }
    } catch (error) {
      console.error('Import error:', error);
      setImportResult({
        total: validationResults.valid.length,
        imported: 0,
        errors: [{ error: error.message }],
      });
    } finally {
      setImporting(false);
    }
  }, [validationResults.valid, dispatch, onSuccess]);

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: dragActive ? 'primary.main' : 'divider',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                bgcolor: dragActive ? 'action.hover' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('poi-file-input').click()}
            >
              <input
                id="poi-file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t('poiImportDropFile') || 'Arraste o arquivo aqui ou clique para selecionar'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Formatos: CSV, XLSX, XLS (max. {MAX_POIS} POIs)
              </Typography>
            </Box>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                component="a"
                href="/templates/poi-import-template.csv"
                download="poi-import-template.csv"
                onClick={(e) => e.stopPropagation()}
              >
                {t('poiExportTemplate') || 'Baixar Template CSV'}
              </Button>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Mapeie as colunas do arquivo para os campos do POI. Campos com * sao obrigatorios.
            </Alert>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="40%">Campo POI</TableCell>
                    <TableCell width="60%">Coluna do Arquivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {POI_FIELDS.map((field) => (
                    <TableRow key={field.id}>
                      <TableCell>
                        {field.label}
                        {field.required && <span style={{ color: 'red' }}> *</span>}
                      </TableCell>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={mapping[field.id] ?? ''}
                            onChange={(e) => handleMappingChange(field.id, e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Nao mapear</em>
                            </MenuItem>
                            {headers.map((header) => (
                              <MenuItem key={header.index} value={header.index}>
                                {header.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {rawData.length} linhas encontradas no arquivo
              </Typography>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            {/* Summary */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${validationResults.valid.length} validos`}
                color="success"
              />
              {validationResults.errors.length > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${validationResults.errors.length} com erro`}
                  color="error"
                />
              )}
            </Box>

            {/* Preview of valid data */}
            {validationResults.valid.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Preview (primeiras {PREVIEW_ROWS} linhas validas):
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome</TableCell>
                        <TableCell>Latitude</TableCell>
                        <TableCell>Longitude</TableCell>
                        <TableCell>Cidade</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationResults.valid.slice(0, PREVIEW_ROWS).map((poi, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{poi.name}</TableCell>
                          <TableCell>{poi.latitude}</TableCell>
                          <TableCell>{poi.longitude}</TableCell>
                          <TableCell>{poi.city || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Errors */}
            {validationResults.errors.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="error" gutterBottom>
                  Linhas com erro (serao ignoradas):
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell width="80">Linha</TableCell>
                        <TableCell>Erros</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationResults.errors.map((error, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{error.line}</TableCell>
                          <TableCell>
                            {error.errors.map((e, i) => (
                              <Typography key={i} variant="body2" color="error">
                                {e}
                              </Typography>
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Import progress/result */}
            {importing && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Importando POIs...
                </Typography>
              </Box>
            )}

            {importResult && (
              <Alert
                severity={importResult.imported === importResult.total ? 'success' : 'warning'}
                sx={{ mb: 2 }}
              >
                {importResult.imported} de {importResult.total} POIs importados com sucesso!
                {importResult.errors.length > 0 && (
                  <Typography variant="body2">
                    {importResult.errors.length} erros durante importacao
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: 500 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          {t('poiImportTitle') || 'Importar POIs'}
        </span>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        {activeStep > 0 && !importResult && (
          <Button onClick={() => setActiveStep((prev) => prev - 1)} disabled={importing}>
            Voltar
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={validateData}
            disabled={!requiredFieldsMapped}
          >
            Validar e Preview
          </Button>
        )}

        {activeStep === 2 && !importResult && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing || validationResults.valid.length === 0}
            startIcon={importing ? null : <CloudUploadIcon />}
          >
            {importing ? 'Importando...' : `Importar ${validationResults.valid.length} POIs`}
          </Button>
        )}

        {importResult && (
          <Button variant="contained" onClick={handleClose}>
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PoiImportDialog;
